import config from "@/services/config";
import axios, { AxiosResponse, AxiosError } from "axios";
import { navigateToLogin, getItemWithExpiry } from "@wavvest/shared-utils";
import { clearAuthStorage } from "@/util/util";

const Service = axios.create({
  baseURL: config.weaveXBaseURL,
  headers: config.headers,
  timeout: config.timeout,
});

Service.interceptors.request.use(
  (config) => {
    const accessToken = getItemWithExpiry("access_token");
    if (accessToken) {
      config.headers.Authorization = accessToken;
    }
    return config;
  },
  (error: AxiosError) => {
    if (
      error.code === "ECONNABORTED" &&
      error.message.indexOf("timeout") !== -1
    ) {
      const originalRequest = error.config;
      if (originalRequest) {
        return Service.request(originalRequest);
      }
    }
    return Promise.reject(error);
  },
);

Service.interceptors.response.use(
  (response: AxiosResponse) => {
    const { status, data } = response;
    if (
      typeof data === "object" &&
      data !== null &&
      ("status" in data || "kind" in data)
    ) {
      if (status === 200 || status === 204) {
        return data;
      } else {
        return Promise.reject(data);
      }
    }
    return data;
  },
  (error: AxiosError) => {
    const { response, status, message: errorMsg } = error;
    const { data } = response || {};
    let errorMessage: string = errorMsg || "Request failed";

    const errorStatusMessages: Record<number, string> = {
      400: "Bad Request",
      403: "You are not authorized to access Weave X. Please log in again.",
      404: `Request URL error: ${response?.config.url}`,
      408: "Request Timeout. Please try logging in again.",
      500: "Internal Server Error. Please try logging in again.",
      501: "Not Implemented. Please try logging in again.",
      502: "Bad Gateway. Please try logging in again.",
      503: "Service Unavailable. Please try logging in again.",
      504: "Gateway Timeout. Please try logging in again.",
      505: "HTTP Version Not Supported",
    };

    if (status) {
      if (status === 401) {
        const { kind, message: detailMessage } = (
          data as { detail: { kind: string; message: string } }
        ).detail;
        errorMessage = detailMessage;
        if (
          kind === "AUTH_TOKEN_INVALID_OR_EXPIRED" ||
          kind === "NO_AUTH_TOKEN"
        ) {
          clearAuthStorage();
          navigateToLogin();
        }
      } else if (errorStatusMessages[status]) {
        errorMessage = errorStatusMessages[status];
      }
    }

    if (error.message.includes("Network Error")) {
      errorMessage = "Network Connection Error";
    } else if (error.code === "ECONNABORTED") {
      errorMessage = "Request Timeout";
    }

    console.log("Weave X Error:", errorMessage);
    return Promise.reject(error);
  },
);

export default Service;
